import { get, set } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import dayjs from 'dayjs'
/**
 * Base class for all classes that will be used in a list such as: sprints, requests, etc.
 * This class should be extended by all classes that will be used in a list.
 * Contains the basic structure for a list item.
 *
 * Structure:
 * - id: The id of the item.
 * - compact: The compact data of the item.
 * - full: The full data of the item.
 * - loaded: The timestamp of the last time the full data was loaded.
 *
 * Also contains:
 * - updateFromServer: Updates the item with the server data.
 * - chgInFull: Helper function to change a value in the full object.
 * - setNewFlag: Sets the loaded flag to -2.
 * - resetLastFullLoad: Resets the loaded flag to the current timestamp.
 *
 * @class LstItmBase
 */

export class BaseItem {
	id = ''
	loaded = -1
	// Values
	compact = {}
	full = {}
	// Values extracted
	modified
	to_delete = false

	constructor(id) {
		this.id = id

		makeObservable(this, {
			id: observable,
			// Values
			compact: observable,
			full: observable,

			// Creator
			createdTs: computed, //x
			setCreatedTs: action.bound,

			createdBy: computed, //x
			setCreatedBy: action.bound,
			setCreator: action.bound,

			// Name
			name: computed, //x
			setName: action.bound,
			displayName: computed,

			// For User
			for_user_id: computed, // x
			setForUserId: action.bound,

			// Progress
			progress: computed, //x
			setProgress: action.bound,

			// Utils
			to_delete: observable, //x
			toDeleteTs: computed,
			setToDelete: action.bound,

			// Modified
			modifiedTs: computed, //x
			setModified: action.bound,

			// Update
			updateFromServer: action, //x
			setNewFlag: action.bound, //x

			// Loaded
			loaded: observable,
			resetLastFullLoad: action.bound //x
		})
	}

	// 1. Core Getters and Setters

	/**
	 * Timestamp when the item was created.
	 * @returns {number} The creation timestamp.
	 */
	get createdTs() {
		return get(this.full, ['createdTs'], get(this.compact, ['createdTs']))
	}

	/**
	 * Sets the creation timestamp to the current time.
	 */
	setCreatedTs = () => {
		this.chgInFull(['createdTs'], dayjs().unix())
	}

	/**
	 * User who created the item (user id).
	 * @returns {string} The id of the user who created the item.
	 */
	get createdBy() {
		return get(
			this.full,
			['createdBy'],
			get(this.compact, ['createdBy'], '')
		)
	}

	/**
	 * Sets the user id of the creator.
	 * @param {string} uid - The user id.
	 */
	setCreatedBy = (uid) => {
		this.chgInFull(['createdBy'], uid)
	}

	/**
	 * Sets the creation timestamp and user id of the creator.
	 * @param {string} uid - The user id.
	 */
	setCreator = (uid) => {
		this.setCreatedTs()
		this.setCreatedBy(uid)
	}

	/**
	 * Name of the item.
	 * @returns {string} The name of the item.
	 */
	get name() {
		return get(this.full, ['name'], get(this.compact, ['name'], ''))
	}

	// Is overridden in the coresponding class
	get displayName() {
		return this.name
	}

	/**
	 * Sets the name of the item.
	 * @param {string} val - The name of the item.
	 */
	setName = (val) => {
		this.chgInFull(['name'], val)
	}

	get for_user_id() {
		return get(
			this.full,
			['for_user_id'],
			get(this.compact, ['for_user_id'], '')
		)
	}

	setForUserId = (val) => {
		this.chgInFull(['for_user_id'], val)
	}

	/**
	 * Progress of the item.
	 * @returns {number} The progress of the item.
	 */
	get progress() {
		return get(
			this.full,
			['progress'],
			get(this.compact, ['progress'], 'draft')
		)
	}

	/**
	 * Sets the progress of the item.
	 * @param {number} val - The progress value.
	 */
	setProgress = (val) => {
		this.chgInFull(['progress'], val)
	}

	// modified comes from the server, added using new Date(), and it comes in the format 2024-08-29 11:12:48. I need to convert it to timestamp
	get modifiedTs() {
		return dayjs(this.modified).unix()
	}

	setModified(modified) {
		this.modified = modified
	}

	/**
	 * toDeleteTs
	 * @returns {number} The to_delete as timestamp.
	 */
	get toDeleteTs() {
		return this.to_delete ? dayjs(this.to_delete).unix() : null
	}

	/**
	 * Sets the to delete flag.
	 * @param {boolean} val The value to set.
	 */
	setToDelete(val) {
		this.to_delete = val
	}

	// 2. Basic Methods applicable to all
	/**
	 * Sets the loaded flag to -2.
	 */
	setNewFlag = () => {
		this.loaded = -2
	}

	/**
	 * Resets the loaded flag to the current timestamp.
	 */
	resetLastFullLoad = () => {
		this.loaded = dayjs().unix()
	}

	/**
	 * Helper function to change a value in the full object.
	 * @param {Array} path - The path to the property in the full object.
	 * @param {any} val - The value to set.
	 */
	chgInFull = (path, val) => {
		if (get(this.full, path) !== val) {
			set(this.full, path, val)
		}
	}

	// override this method in the child class
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	updateFromServerCompact(compactJson) {
		// only compact data is updated
	}

	// override this method in the child class
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	updateFromServerFull(fullJson) {
		// only full data is updated
	}

	/**
	 * Updates the item with the data from the server.
	 * @param {object} srvJson - The server data.
	 */
	updateFromServer(srvJson) {
		if (srvJson?.compact) {
			if (typeof srvJson.compact === 'string') {
				try {
					this.compact = JSON.parse(srvJson.compact)
				} catch (error) {
					console.error('Failed to parse compact JSON:', error)
					this.compact = {} // Handle the error as needed
				}
			} else {
				this.compact = srvJson.compact
			}

			this.updateFromServerCompact(this.compact)
		}
		if (srvJson?.full) {
			if (typeof srvJson.full === 'string') {
				try {
					this.full = JSON.parse(srvJson.full)
				} catch (error) {
					console.error('Failed to parse full JSON:', error)
					this.full = {} // Handle the error as needed
				}
			} else {
				this.full = srvJson.full
			}
			this.updateFromServerFull(this.full)
			this.resetLastFullLoad()
		}

		// Let's add to_delete, modified to the item
		this.to_delete = srvJson?.to_delete || false
		this.modified = srvJson?.modified || null
	}
}
